import { DumbAutoSetting, KeySelectionOptions, SmartAutoSetting } from "../models/KeySelectionOptions.model";
import { findNoteByHalfsteps, NoteSettingsDecision, printScaleRootLetter, printScaleShapeType } from "../models/wasm.model";
import { AudioHistoryState, findPrimaryScalePastNTicks } from "../statemachines/AudioHistoryState";

export default function chooseAutomaticNoteOverrides(
  audioHistory: AudioHistoryState,
  priorSettings: Array<NoteSettingsDecision>,
  keySelectSettings: KeySelectionOptions
): Array<NoteSettingsDecision> {
  // Pre-requisites:
  //   One scale is dominating the history   (could be 2 nearby scales, but that's fancier)
  //   History is at least 4 seconds long

  if (audioHistory.newestTimestampIndex < 10) {
    // Don't do autopinning for the first 5 seconds of the song.
    // (1) there isn't enough information
    // (2) if it's a re-do and automation is enabled, the user likely wants the pins preserved
    return [];
  }

  // detecting a primary scale change:
  //  (1) what primary scale was considered during prior overrides
  //
  const primaryScale = findPrimaryScalePastNTicks(audioHistory, 20);
  if (!primaryScale) {
    return [];
  }

  let overrides: Array<NoteSettingsDecision> = [...priorSettings];

  // if all prior settings match manual or the current primary scale, return []
  const manualOverrides = overrides.filter((o) => o.why[0] === "manual");
  const autoOverrides = overrides.filter((o) => o.why[0] !== "manual");
  const anyDifferentScales =
    autoOverrides.length === 0 ||
    autoOverrides.some((o) => o.primaryScale?.family !== primaryScale.family && o.primaryScale?.shape !== primaryScale.shape);

  if (!anyDifferentScales) {
    // If the primary scale did not change, no need to re-do automation
    return [];
  } else {
    // If it did change, discard any automation relating to old primary scale, and recalculate
    overrides = [...manualOverrides];
  }

  // remove any prior settings related to a different primary scale
  // preserve any manual settings

  const originalOverridesLength = overrides.length;
  const minorTonic = primaryScale.family.minor_tonic_id;
  const { isPreferMajor, autoPin } = keySelectSettings;
  const scaleName = `${printScaleRootLetter(primaryScale, isPreferMajor)} ${printScaleShapeType(primaryScale, isPreferMajor)}`;

  const ignoreMin7UpFromMajor = isPreferMajor && autoPin.wrong7thIgnore !== SmartAutoSetting.Manual;
  const ignoreHalfStepUpFromMinor = !isPreferMajor && autoPin.dim2ndIgnore === DumbAutoSetting.Auto;

  if (ignoreMin7UpFromMajor || ignoreHalfStepUpFromMinor) {
    const note_id = findNoteByHalfsteps(minorTonic, 1);
    if (!overrides.find((o) => o.settings.note_id === note_id)) {
      overrides.push({
        settings: {
          note_id,
          scale_override: "OutOfScale",
        },
        primaryScale,
        why: [
          ignoreMin7UpFromMajor ? `${scaleName}: Major scales do not include a minor seventh.` : "",
          ignoreHalfStepUpFromMinor ? `${scaleName}: No scale includes a 1/2 step interval above the root.` : "",
        ].filter((x) => x),
      });
    }
  }

  const ignoreHalfStepUpFromMajor = isPreferMajor && autoPin.dim2ndIgnore === DumbAutoSetting.Auto;
  const ignoreMaj3rdUpFromMinor = !isPreferMajor && autoPin.wrong3rdIgnore === DumbAutoSetting.Auto;

  if (ignoreHalfStepUpFromMajor || ignoreMaj3rdUpFromMinor) {
    const note_id = findNoteByHalfsteps(minorTonic, 4);
    if (!overrides.find((o) => o.settings.note_id === note_id)) {
      overrides.push({
        settings: {
          note_id,
          scale_override: "OutOfScale",
        },
        primaryScale,
        why: [
          ignoreHalfStepUpFromMajor ? `${scaleName}: No scale includes a 1/2 step interval above the root.` : "",
          ignoreMaj3rdUpFromMinor ? `${scaleName}: Minor scales never include a major third above the root.` : "",
        ],
      });
    }
  }

  const ignoreFlat5UpFromMinor =
    !isPreferMajor &&
    (autoPin.flat5Ignore === SmartAutoSetting.AlwaysAuto ||
      (autoPin.flat5Ignore === SmartAutoSetting.ContextualAuto && !["Blues", "Pentatonic"].includes(primaryScale.shape)));
  // console.log(
  //   "thinking about minor flat5 - scale shape=" + primaryScale.shape + "  targetting=" + findNoteByHalfsteps(minorTonic, 6)
  // );

  // FIXME: wrong3 ignore could actually be a smart setting in the case of major... Jamming flat5 ignore together with it here may confuse
  const ignoreMin3rdUpFromMajor =
    isPreferMajor &&
    (autoPin.wrong3rdIgnore === DumbAutoSetting.Auto ||
      (autoPin.flat5Ignore === SmartAutoSetting.ContextualAuto && !["Blues", "Pentatonic"].includes(primaryScale.shape)));

  if (ignoreMin3rdUpFromMajor || ignoreFlat5UpFromMinor) {
    const note_id = findNoteByHalfsteps(minorTonic, 6);
    if (!overrides.find((o) => o.settings.note_id === note_id)) {
      overrides.push({
        settings: {
          note_id,
          scale_override: "OutOfScale",
        },
        primaryScale,
        why: [
          ignoreMin3rdUpFromMajor ? `${scaleName}: Major scales (other than blues) do not include a minor third above the root.` : "",
          ignoreFlat5UpFromMinor ? `${scaleName}: Minor scales (other than blues) do not include a flat 5th above the root.` : "",
          autoPin.flat5Ignore === SmartAutoSetting.ContextualAuto
            ? "The detected scale included notes incompatible with blues or pentatonic scales."
            : "",
        ].filter((x) => x),
      });
    }
  }

  const ignoreFlat5UpFromMajor = isPreferMajor && autoPin.flat5Ignore !== SmartAutoSetting.Manual;

  const ignoreMaj6UpFromMinor =
    !isPreferMajor &&
    (autoPin.wrong6thIgnore === SmartAutoSetting.AlwaysAuto ||
      (autoPin.wrong6thIgnore === SmartAutoSetting.ContextualAuto && "MelodicMinor" !== primaryScale.shape));

  if (ignoreFlat5UpFromMajor || ignoreMaj6UpFromMinor) {
    const note_id = findNoteByHalfsteps(minorTonic, 9);

    if (!overrides.find((o) => o.settings.note_id === note_id)) {
      overrides.push({
        settings: {
          note_id,
          scale_override: "OutOfScale",
        },
        primaryScale,
        why: [
          ignoreFlat5UpFromMajor ? `${scaleName}: Major scales never include a flat 5th above the root.` : "",
          ignoreMaj6UpFromMinor ? `${scaleName}: Minor scales (other than melodic) do not include a major 6th above the root` : "",
          ignoreMaj6UpFromMinor && autoPin.wrong6thIgnore === SmartAutoSetting.ContextualAuto
            ? "Melodic minor was not detected, as of time of auto-pinning decision."
            : "",
        ].filter((x) => x),
      });
    }
  }

  // FIXME: For contextual, it isn't enough to check the scale type, because it's broader.
  // Better to compare the weight of the 2 possible intervals
  const ignoreMin6UpFromMajor =
    isPreferMajor && [SmartAutoSetting.AlwaysAuto, SmartAutoSetting.ContextualAuto].includes(autoPin.wrong6thIgnore);
  const ignoreMaj7UpFromMinor =
    !isPreferMajor &&
    (autoPin.wrong7thIgnore === SmartAutoSetting.AlwaysAuto ||
      (autoPin.wrong7thIgnore === SmartAutoSetting.ContextualAuto && !["MelodicMinor", "HarmonicMinor"].includes(primaryScale.shape)));

  if (ignoreMin6UpFromMajor || ignoreMaj7UpFromMinor) {
    const note_id = findNoteByHalfsteps(minorTonic, 11);

    if (!overrides.find((o) => o.settings.note_id === note_id)) {
      overrides.push({
        settings: {
          note_id,
          scale_override: "OutOfScale",
        },
        primaryScale,
        why: [
          ignoreMin6UpFromMajor ? `${scaleName}: Major scales do not include a minor 6th above the root` : "",
          ignoreMaj7UpFromMinor ? `${scaleName}: Minor scales (other than harmonic/melodic) do not include a major 7th above the root` : "",
          ignoreMaj7UpFromMinor && autoPin.wrong6thIgnore === SmartAutoSetting.ContextualAuto
            ? "Melodic and Harmonic minor were not detected, as of time of auto-pinning decision."
            : "",
        ].filter((x) => x),
      });
    }
  }
  if (overrides.length > originalOverridesLength) {
    return overrides;
  }
  return [];
}
